import styled, { css } from "styled-components";
import { type IntlShape, useIntl } from "react-intl";
import {
  type MouseEvent,
  type PropsWithChildren,
  type ComponentProps,
  type ReactNode,
} from "react";
import { useHref } from "react-router";
import { useLinkClickHandler } from "react-router";
import useSearch from "src/utils/hooks/useSearch";
import { tripHashFromSearchResponse } from "src/utils/location/createTripHashForCard";
import type { DistanceSystem } from "src/utils/distanceSystem";
import color, { primitive } from "src/design-system/tokens/color";
import type { SupportedCurrencyCode } from "src/utils/currency";
import { modeMessages } from "src/domain/SegmentScreen/DynamicSchedules/Schedule/Schedule.messages";
import { CarrierLineNames } from "src/domain/SegmentScreen/CarrierSection/CarrierLineNames/CarrierLineNames";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import { iconSize } from "src/design-system/tokens/iconSize";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { TransitIcon } from "src/design-system/components/TransitIcon/TransitIcon";
import { fontSize, spacing } from "../../../../theme";
import type { DetailCell } from "../cell/DetailCell";
import useUser from "../../../../utils/hooks/useUser";
import {
  FormattedDuration,
  getFormattedDurationText,
} from "../../../FormattedDuration/FormattedDuration";
import { formatFrequency } from "../../../Frequency/Frequency";
import { formatDistance } from "../../../FormattedDistance/FormattedDistance";
import type { Kind } from "../../../../api/Kind";
import {
  formatPriceRange,
  FormattedPriceRange,
} from "../../../FormattedPriceRange/FormattedPriceRange";
import { Timeline } from "../../Timeline";
import { useTypedLocation } from "../../../../utils/hooks/useTypedLocation";
import { useRouteCanonical } from "../../../../utils/hooks/useRoute";
import { hashChange } from "../../../../utils/location/hashChange";
import messages from "./TroniconTimelineDetail.messages";

type Props = {
  context: "transport" | "tripPlanner";
  cell: DetailCell;
  onClick?: (segmentIndex: number) => void;
  onHover: (segmentIndex?: number) => void;
  className?: string;
  isCompact?: boolean;
  timeline?: ReactNode;
  origin?: string;
  destination?: string;
};

export function TroniconTimelineDetail(props: Props) {
  const hasCta = props.onClick !== undefined;
  const intl = useIntl();
  const { distanceSystem, currencyCode } = useUser();

  const ariaLabel = getAriaLabelForDetailCell(
    props.cell,
    intl,
    distanceSystem,
    currencyCode,
    props.origin,
    props.destination,
  );

  return (
    <Container
      context={props.context}
      onClick={props.onClick}
      className={props.className}
      onMouseEnter={() => props.onHover(props.cell.segmentIndex)}
      onMouseLeave={() => props.onHover(undefined)}
      segmentIndex={props.cell.segmentIndex}
      hasCta={hasCta}
      aria-label={ariaLabel}
    >
      {props.timeline ?? (
        <FullWidthTimeline type="detail" transitMode={props.cell.transitMode} />
      )}
      <LeftAligned $isCompact={props.isCompact}>
        <TopDetailLine cell={props.cell} />
        {props.cell.lineNames.length > 0 && (
          <CarrierLineNames lineNames={props.cell.lineNames} noButton />
        )}
        {props.cell.priceRange && (
          <PriceRangeDetailText {...props.cell.priceRange} />
        )}
      </LeftAligned>
      {hasCta && (
        <RightAligned>
          <ChevronRight
            width={iconSize.md}
            height={iconSize.md}
            color={color.icon.brand}
          />
        </RightAligned>
      )}
    </Container>
  );
}

function getAriaLabelForDetailCell(
  cell: DetailCell,
  intl: IntlShape,
  distanceSystem: DistanceSystem,
  currencyCode: SupportedCurrencyCode,
  origin?: string,
  destination?: string,
) {
  const duration = getFormattedDurationText(cell.durationInMinutes, intl);
  const { additionalInfo } = getTimelineDetailTop(cell, intl, distanceSystem);
  const priceText = cell.priceRange
    ? formatPriceRange(
        intl,
        cell.priceRange.low,
        cell.priceRange.high,
        currencyCode,
      )
    : "";

  const fromToText = intl.formatMessage(messages.fromTo, {
    origin,
    destination,
  });
  return `${fromToText} ${duration} ${additionalInfo} ${priceText}`;
}

type ContainerProps = PropsWithChildren<{
  context: "transport" | "tripPlanner";
  onClick?: (segmentIndex: number) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  segmentIndex: number;
  hasCta: boolean;
  title?: string;
  className?: string;
}>;

function Container({
  context,
  onClick,
  segmentIndex,
  ...props
}: ContainerProps) {
  const routeCanonical = useRouteCanonical();
  const { searchResponse } = useSearch();
  const tripHash = tripHashFromSearchResponse(searchResponse);
  const hash =
    context === "tripPlanner"
      ? `${tripHash}/r/${routeCanonical}/s/${segmentIndex}`
      : `#r/${routeCanonical}/s/${segmentIndex}`;

  const location = useTypedLocation();
  const { state, ...to } = hashChange(hash, location);

  /*
  We can't use react-router's <Link> component because it doesn't give us the standard
  behaviour of an anchor tag that we want eg. ctrl+click opens the link in a new tab,
  but also navigates to the link in the current tab. Instead we need to use an anchor
  tag with the helper hook useLinkClickHandler.
  */
  const href = useHref(to);
  const linkClickHandler = useLinkClickHandler(to, { state });

  // Handler for react-router navigation on an anchor tag.
  function handleLinkClick(event: MouseEvent<HTMLAnchorElement>) {
    handleClick();
    linkClickHandler(event);
  }

  // Original handler.
  function handleClick() {
    onClick && onClick(segmentIndex);
  }

  if (props.hasCta) {
    return (
      <ContainerLink
        onClick={handleLinkClick}
        href={href}
        {...props}
        title={props.title}
      />
    );
  } else {
    return <ContainerDiv {...props} />;
  }
}

const FullWidthTimeline = styled(Timeline)<ComponentProps<typeof Timeline>>`
  flex-shrink: 0;
`;

function getTimelineDetailTop(
  cell: DetailCell,
  intl: IntlShape,
  distanceSystem: DistanceSystem,
) {
  const frequency = cell.frequencyPerWeek
    ? formatFrequency(cell.frequencyPerWeek, intl)
    : undefined;
  const distance =
    cell.distanceInMeters && showDistance(cell.vehicleKind)
      ? formatDistance(cell.distanceInMeters, distanceSystem, intl)
      : undefined;

  // Walks and Flights shouldn’t have "On demand" as frequency
  const isPlane = cell.vehicleKind === "plane";
  const isWalk = cell.vehicleKind === "walk";
  const isOnDemand = cell.frequencyPerWeek && cell.frequencyPerWeek <= 0;
  const hideFrequency = isOnDemand && (isWalk || isPlane);
  const byTransitMode = intl.formatMessage(modeMessages[cell.transitMode]);

  let additionalInfo = [byTransitMode];
  !hideFrequency && frequency && additionalInfo.push(frequency);
  distance && additionalInfo.push(distance);

  return { additionalInfo };
}

function TopDetailLine({ cell }: { cell: DetailCell }) {
  const intl = useIntl();
  const { distanceSystem } = useUser();

  const { additionalInfo } = getTimelineDetailTop(cell, intl, distanceSystem);

  const { isMobile } = useScreenMinWidth();

  return (
    <DetailText>
      {isMobile && cell.transitMode === "walk" ? (
        <div className="flex items-center gap-x-lg">
          <TransitIcon mode="walk" className="w-4" />
          <div className="text-text-secondary">
            <FormattedDuration totalMinutes={cell.durationInMinutes} />
          </div>
        </div>
      ) : (
        <FormattedDuration totalMinutes={cell.durationInMinutes} />
      )}

      {additionalInfo.map((text, i) => {
        return (
          <span
            key={i}
            className={
              isMobile && cell.transitMode == "walk"
                ? "text-text-secondary"
                : ""
            }
          >
            <DotSpan>&nbsp;•&nbsp;</DotSpan>
            {text}
          </span>
        );
      })}
    </DetailText>
  );
}

function showDistance(vehicleKind: Kind): boolean {
  const showDistance = [
    "walk",
    "foot",
    "car",
    "taxi",
    "rideshare",
    "towncar",
    "animal",
    "bicycle",
    "cycle",
  ];

  return showDistance.includes(vehicleKind);
}

const DetailText = styled.span`
  font-size: ${fontSize.h6};
  line-height: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const DotSpan = styled.span`
  word-spacing: 4;
  color: #707070;
`;

const PriceRangeDetailText = styled(FormattedPriceRange)`
  font-size: ${fontSize.h6};
  line-height: 24px;
`;

const RightAligned = styled.div`
  align-self: center;
`;

const LeftAligned = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  overflow: hidden;
  padding-top: ${(props) => (props.$isCompact ? spacing.lg : spacing.xxl)};
  padding-bottom: ${(props) => (props.$isCompact ? spacing.lg : spacing.xxl)};
  padding-left: 16px;
  z-index: 1;

  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const containerStyles = css<{ hasCta: boolean }>`
  min-height: 56px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding-left: 16px;
  cursor: ${(props) => (props.hasCta ? "pointer" : "default")};
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;

  border-radius: ${spacing.md};
  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &:focus {
    outline: none;
    &::before {
      outline: 3px solid ${primitive.blue_500};
      border-radius: ${spacing.md};
    }
  }
`;

const ContainerDiv = styled.div<{ hasCta: boolean }>`
  ${containerStyles}
`;

const ContainerLink = styled.a<{ hasCta: boolean }>`
  color: inherit;
  text-decoration: none;
  ${containerStyles}
`;
